import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Callout, Checkbox, Flex, Grid, Text, TextField } from '@radix-ui/themes'
import { useTRPC } from '~/utils/trpc'
import { nowTime, todayIso } from '~/utils/date'
import {
  buildDataSourceInput,
  dataSourceReuseWarning,
  dataSourceStatusWarning,
  initialDataSourceValues,
  validateDataSourceValues,
  type MeterDataSourceFieldValues
} from '~/features/installations/dataSource'
import { MeterDataSourceFields } from './MeterDataSourceFields'

interface AddDataSourceFormProps {
  installationId: number
  defaultLoggerMaker: string | null
  defaultSerialNumber: string | null
  defaultPulseWeight: number | null
  // Pre-fills the Shayp device description (orgName_SiteName_StationName).
  defaultDeviceDescription: string
  // Called after a successful add (no warning) or when the user dismisses the form.
  onDone: () => void
  // Labels the secondary button "Skip" instead of "Cancel" (post-link flow).
  showSkip?: boolean
}

export const AddDataSourceForm = ({
  installationId,
  defaultLoggerMaker,
  defaultSerialNumber,
  defaultPulseWeight,
  defaultDeviceDescription,
  onDone,
  showSkip = false
}: AddDataSourceFormProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [values, setValues] = useState<MeterDataSourceFieldValues>(() =>
    initialDataSourceValues(defaultLoggerMaker, defaultSerialNumber, defaultPulseWeight, defaultDeviceDescription)
  )
  const [createIndex, setCreateIndex] = useState(false)
  const [indexValue, setIndexValue] = useState('')
  const [indexDate, setIndexDate] = useState(todayIso())
  const [indexTime, setIndexTime] = useState(nowTime())
  const [warning, setWarning] = useState<string | null>(null)

  const mutation = useMutation(
    trpc.ops.stationInstallations.addDataSource.mutationOptions({
      onSuccess: (result) => {
        const failWarning = dataSourceStatusWarning(result.dataSourceStatus, values)
        if (failWarning) {
          setWarning(failWarning)
          return
        }
        // Data source created: refresh, then keep the dialog open to surface the
        // reuse note (device already existed) if there is one; otherwise close.
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.get.queryKey({ installationId }) })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        const reuseWarning = dataSourceReuseWarning(result)
        if (reuseWarning) {
          setWarning(reuseWarning)
          return
        }
        onDone()
      }
    })
  )

  // Only Shayp installations take an initial index; other makers ignore it.
  const showIndex = values.maker === 'shayp'

  const validationError = (): string | null => {
    const fieldError = validateDataSourceValues(values)
    if (fieldError) return fieldError
    if (showIndex && createIndex) {
      if (!(Number(indexValue) >= 0) || indexValue.trim() === '') return 'Index value must be a positive number'
      if (!/^\d{4}-\d{2}-\d{2}$/.test(indexDate)) return 'Index date is invalid'
      if (!/^\d{2}:\d{2}$/.test(indexTime)) return 'Index time is invalid'
    }
    return null
  }

  const error = validationError()

  const handleSubmit = () => {
    if (error) return
    const index =
      showIndex && createIndex
        ? { value: Number(indexValue), readingAt: new Date(`${indexDate}T${indexTime}:00`).toISOString() }
        : undefined
    mutation.mutate({ installationId, dataSource: buildDataSourceInput(values, index) })
  }

  return (
    <Flex direction="column" gap="3" mt="4">
      <MeterDataSourceFields values={values} onChange={setValues} />

      {showIndex ? (
        <Box className="border-t border-t-[color:var(--gray-6)] pt-3">
          <label className="flex items-center gap-2 mb-3">
            <Checkbox checked={createIndex} onCheckedChange={(v) => setCreateIndex(v === true)} />
            <Text size="2" weight="medium">
              Record initial index
            </Text>
          </label>
          {createIndex ? (
            <Grid columns="3" gap="3">
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Index value
                </Text>
                <TextField.Root type="number" value={indexValue} onChange={(e) => setIndexValue(e.target.value)} />
              </Box>
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Date
                </Text>
                <TextField.Root
                  type="date"
                  value={indexDate}
                  max={todayIso()}
                  onChange={(e) => setIndexDate(e.target.value)}
                />
              </Box>
              <Box>
                <Text as="label" size="1" color="gray" className="block mb-1">
                  Time
                </Text>
                <TextField.Root type="time" value={indexTime} onChange={(e) => setIndexTime(e.target.value)} />
              </Box>
            </Grid>
          ) : null}
        </Box>
      ) : null}

      {warning ? (
        <Callout.Root color="amber">
          <Callout.Text>{warning}</Callout.Text>
        </Callout.Root>
      ) : null}

      {mutation.error ? (
        <Callout.Root color="red">
          <Callout.Text>{mutation.error.message}</Callout.Text>
        </Callout.Root>
      ) : null}

      <Flex gap="2" justify="end">
        <Button variant="soft" color="gray" onClick={onDone}>
          {showSkip ? 'Skip' : 'Cancel'}
        </Button>
        <Button
          variant="solid"
          disabled={!!error || mutation.isPending}
          loading={mutation.isPending}
          onClick={handleSubmit}
        >
          Add
        </Button>
      </Flex>
    </Flex>
  )
}
